import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useSyncExternalStore } from "react"
import { useAuth, type HqUser } from "./auth"
import { saveMyMetadata } from "./account-metadata"
import { NAV, type NavItem } from "./nav"
import { ROLE_TABS, useMyRole } from "./roles"

/**
 * The phone's bottom bar, chosen by each person (owners, 16 Oct 2026): Today and More stay, and up to
 * four sections of their own sit between them. Like the language and the favourites it follows the
 * PERSON: kept on their Medusa account (user metadata `hq_tabs` + `hq_tabs_at`) and on the device, the
 * newer choice winning. A role given in Settings → Team (lib/roles.ts) counts as a choice made when it
 * was given: the person's bar becomes the role's, and they can change it again afterwards.
 */

export const MAX_TABS = 4
export const DEFAULT_TABS = ["/orders", "/custom", "/stock"]
const KEY = "hq-tabs"
type Saved = { paths: string[]; at: number }

/** Sections that can be a tab: any but Today (always first) — once each, at most four. */
const CHOOSABLE = new Set(NAV.map((item) => item.to).filter((to) => to !== "/"))
export const cleanTabs = (paths: unknown): string[] =>
  Array.isArray(paths) ? [...new Set(paths.filter((path): path is string => typeof path === "string" && CHOOSABLE.has(path)))].slice(0, MAX_TABS) : DEFAULT_TABS

function readLocal(): Saved | null {
  try {
    const raw = JSON.parse(window.localStorage.getItem(KEY) ?? "null") as Saved | null
    return raw && Array.isArray(raw.paths) ? { paths: cleanTabs(raw.paths), at: Number(raw.at) || 0 } : null
  } catch {
    return null
  }
}

function writeLocal(saved: Saved) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(saved))
  } catch {
    // private mode: the account still keeps them
  }
}

function accountTabs(user: HqUser | null): Saved | null {
  const metadata = user?.metadata ?? null
  if (!metadata || !Array.isArray(metadata.hq_tabs)) return null
  return { paths: cleanTabs(metadata.hq_tabs), at: Number(metadata.hq_tabs_at) || 0 }
}

let current: Saved = { paths: DEFAULT_TABS, at: 0 }
let loaded = false
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((listener) => listener())

function ensureLoaded() {
  if (loaded || typeof window === "undefined") return
  loaded = true
  current = readLocal() ?? current
}

function subscribe(listener: () => void) {
  ensureLoaded()
  listeners.add(listener)
  const onStorage = (event: StorageEvent) => {
    if (event.key !== KEY) return
    current = readLocal() ?? { paths: DEFAULT_TABS, at: 0 }
    emit()
  }
  window.addEventListener("storage", onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener("storage", onStorage)
  }
}

const snapshot = () => {
  ensureLoaded()
  return current
}

function set(next: Saved) {
  current = next
  writeLocal(next)
  emit()
}

async function saveToAccount(user: HqUser, saved: Saved, queryClient: ReturnType<typeof useQueryClient>) {
  await saveMyMetadata(queryClient, user, { hq_tabs: saved.paths, hq_tabs_at: saved.at })
}

/** The person's tabs (between Today and More) and how to change them. */
export function useTabs() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const saved = useSyncExternalStore(subscribe, snapshot, () => current)
  const account = accountTabs(user)
  const { role, entry } = useMyRole()
  const defaults = ROLE_TABS[role]

  useEffect(() => {
    if (!user) return
    if (entry && entry.at > Math.max(current.at, account?.at ?? 0)) {
      const next = { paths: cleanTabs(ROLE_TABS[entry.role]), at: entry.at }
      set(next)
      void saveToAccount(user, next, queryClient).catch(() => undefined)
    } else if (account && account.at > current.at) set(account)
    else if (current.at > (account?.at ?? 0)) void saveToAccount(user, current, queryClient).catch(() => undefined)
  }, [user?.id, account?.at, entry?.at]) // eslint-disable-line react-hooks/exhaustive-deps

  const choose = useCallback(
    (paths: string[]) => {
      const next = { paths: cleanTabs(paths), at: Date.now() }
      set(next)
      if (user) void saveToAccount(user, next, queryClient).catch(() => undefined)
    },
    [user, queryClient]
  )

  const items = saved.paths.map((path) => NAV.find((item) => item.to === path)).filter((item): item is NavItem => !!item)
  return { paths: saved.paths, items, choose, defaults, choosable: NAV.filter((item) => CHOOSABLE.has(item.to)) }
}
